import React, { useState, useEffect } from 'react'

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--bg)',
  surf: 'var(--surf)',
  surf2: 'var(--surf2)',
  border: 'var(--border)',
  text: 'var(--text)',
  muted: 'var(--muted)',
  dim: 'var(--dim)',
  amber: 'var(--accent)',
  amberBg: 'var(--accent-bg)',
  amberBorder: 'var(--accent-border)',
  green: 'var(--success)',
}

const AF = {
  h: "'Space Grotesk', system-ui, sans-serif",
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
}

// ── Mobile hook ──────────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

// ── SVG Pattern Defs (shared global defs, used by non-hero sections) ─────────
function SvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <pattern id="m-nordic" width="56" height="56" patternUnits="userSpaceOnUse">
          <polygon points="28,12 40,28 28,44 16,28" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
          <polygon points="28,20 34,28 28,36 22,28" fill="var(--accent)" opacity="0.25"/>
          <polygon points="2,40 8,28 14,40" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
          <polygon points="42,40 48,28 54,40" fill="none" stroke="var(--accent)" strokeWidth="1.1"/>
          <circle cx="0" cy="28" r="1.4" fill="var(--accent)"/>
          <circle cx="56" cy="28" r="1.4" fill="var(--accent)"/>
          <circle cx="28" cy="0" r="1.4" fill="var(--accent)"/>
          <circle cx="28" cy="56" r="1.4" fill="var(--accent)"/>
        </pattern>
      </defs>
    </svg>
  )
}

// ── Andalucian decor — Alhambra 8-pointed star tessellation ──────────────────
function AndalucianDecor({ isDark }: { isDark: boolean }) {
  const S = 60
  const COLS = 7, ROWS = 9
  const W = COLS * S + S, H = ROWS * S + S

  function starPts(cx: number, cy: number, outer: number, inner: number): string {
    return Array.from({ length: 16 }, (_, i) => {
      const a = (i * Math.PI / 8) - Math.PI / 2
      const r = i % 2 === 0 ? outer : inner
      return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`
    }).join(' ')
  }

  const stars: { cx: number; cy: number }[] = []
  for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
    stars.push({ cx: c * S + S / 2, cy: r * S + S / 2 })
  }

  const outerFill = isDark ? '#C8721A' : '#B43600'
  const outerFillOp = isDark ? 0.07 : 0.055
  const outerStroke = isDark ? '#D4852A' : '#A83000'
  const outerStrokeOp = isDark ? 0.65 : 0.5
  const innerStroke = isDark ? '#E9AA30' : '#B43600'
  const innerStrokeOp = isDark ? 0.5 : 0.4
  const gemFill = isDark ? '#E9AA30' : '#B43600'
  const lozengeStroke = isDark ? '#D4852A' : '#A83000'
  const lozengeOp = isDark ? 0.4 : 0.3

  return (
    <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true"
      style={{ position: 'absolute', right: 0, top: '-5%', height: '110%', width: 'auto', pointerEvents: 'none' }}>
      <defs>
        <linearGradient id="and-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="white" stopOpacity="0"/>
          <stop offset="28%" stopColor="white" stopOpacity="0.6"/>
          <stop offset="55%" stopColor="white" stopOpacity="1"/>
          <stop offset="100%" stopColor="white" stopOpacity="1"/>
        </linearGradient>
        <mask id="and-mask">
          <rect width="100%" height="100%" fill="url(#and-fade)"/>
        </mask>
      </defs>
      <g mask="url(#and-mask)">
        {stars.map(({ cx, cy }, i) => (
          <g key={i}>
            <polygon points={starPts(cx, cy, 22, 9)}
              fill={outerFill} fillOpacity={outerFillOp}
              stroke={outerStroke} strokeWidth="1.15" strokeOpacity={outerStrokeOp}/>
            <polygon points={starPts(cx, cy, 10.5, 4.3)}
              fill="none" stroke={innerStroke} strokeWidth="0.75" strokeOpacity={innerStrokeOp}/>
            <circle cx={cx} cy={cy} r={2} fill={gemFill} fillOpacity="0.35"/>
          </g>
        ))}
        {stars.filter(({ cx }) => cx + S < W).map(({ cx, cy }, i) => {
          const mx = cx + S / 2
          return (
            <polygon key={`h${i}`}
              points={`${mx},${cy - 9} ${mx + 9},${cy} ${mx},${cy + 9} ${mx - 9},${cy}`}
              fill="none" stroke={lozengeStroke} strokeWidth="0.9" strokeOpacity={lozengeOp}/>
          )
        })}
        {stars.filter(({ cy }) => cy + S < H).map(({ cx, cy }, i) => {
          const my = cy + S / 2
          return (
            <polygon key={`v${i}`}
              points={`${cx},${my - 9} ${cx + 9},${my} ${cx},${my + 9} ${cx - 9},${my}`}
              fill="none" stroke={lozengeStroke} strokeWidth="0.9" strokeOpacity={lozengeOp}/>
          )
        })}
      </g>
    </svg>
  )
}

// ── Finnish Kalevala rune-wheel grid ─────────────────────────────────────────
function FinnishDecor({ isDark }: { isDark: boolean }) {
  const S = 66
  const COLS = 6, ROWS = 8
  const W = COLS * S + S * 0.5, H = ROWS * S

  const stk = isDark ? '#3B8EEA' : '#1F5FB8'
  const sop = isDark ? 0.50 : 0.40
  const fop = isDark ? 0.07 : 0.05
  const gem = isDark ? '#7BCFFF' : '#1F5FB8'

  const cells: { cx: number; cy: number }[] = []
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      const offset = row % 2 === 1 ? S / 2 : 0
      cells.push({ cx: col * S + S / 2 + offset, cy: row * S + S / 2 })
    }
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true"
      style={{ position: 'absolute', right: 0, top: '-5%', height: '110%', width: 'auto', pointerEvents: 'none' }}>
      <defs>
        <linearGradient id="fi-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"  stopColor="white" stopOpacity="0"/>
          <stop offset="30%" stopColor="white" stopOpacity="0.65"/>
          <stop offset="55%" stopColor="white" stopOpacity="1"/>
          <stop offset="100%" stopColor="white" stopOpacity="1"/>
        </linearGradient>
        <mask id="fi-mask">
          <rect width="100%" height="100%" fill="url(#fi-fade)"/>
        </mask>
      </defs>
      <g mask="url(#fi-mask)">
        {cells.map(({ cx, cy }, idx) => {
          const R = 24, r2 = 16, r3 = 9
          return (
            <g key={idx}>
              <circle cx={cx} cy={cy} r={R} fill={stk} fillOpacity={fop} stroke={stk} strokeWidth="1.1" strokeOpacity={sop}/>
              <circle cx={cx} cy={cy} r={r2} fill="none" stroke={stk} strokeWidth="0.7" strokeOpacity={sop * 0.7}/>
              {Array.from({ length: 8 }, (_, k) => {
                const a = (k * Math.PI) / 4
                return (
                  <line key={k}
                    x1={(cx + r3 * Math.cos(a)).toFixed(1)} y1={(cy + r3 * Math.sin(a)).toFixed(1)}
                    x2={(cx + R * Math.cos(a)).toFixed(1)}  y2={(cy + R * Math.sin(a)).toFixed(1)}
                    stroke={stk} strokeWidth="0.8" strokeOpacity={sop * 0.85}/>
                )
              })}
              {Array.from({ length: 4 }, (_, k) => {
                const a1 = (k * Math.PI) / 2, a2 = ((k + 1) * Math.PI) / 2
                const mx = (cx + r3 * Math.cos((a1 + a2) / 2)).toFixed(1)
                const my = (cy + r3 * Math.sin((a1 + a2) / 2)).toFixed(1)
                return (
                  <path key={`arc${k}`}
                    d={`M${(cx + r3 * Math.cos(a1)).toFixed(1)},${(cy + r3 * Math.sin(a1)).toFixed(1)} Q${mx},${my} ${(cx + r3 * Math.cos(a2)).toFixed(1)},${(cy + r3 * Math.sin(a2)).toFixed(1)}`}
                    fill="none" stroke={stk} strokeWidth="0.6" strokeOpacity={sop * 0.45}/>
                )
              })}
              <circle cx={cx} cy={cy} r={3} fill={gem} fillOpacity={0.4}/>
            </g>
          )
        })}
      </g>
    </svg>
  )
}

// ── Porto azulejo tile panel ──────────────────────────────────────────────────
function PortugalDecor({ isDark }: { isDark: boolean }) {
  const T = 68
  const G = 4
  const COLS = 5, ROWS = 8
  const W = COLS * (T + G) + G
  const H = ROWS * (T + G) + G

  // Light mode: authentic azulejo — cream tiles with deep cobalt patterns
  const groutBg = isDark ? '#09192E' : '#C8D8EC'
  const tileFill = isDark ? '#19509E' : '#EEF4FF'
  const lineColor = isDark ? '#E8EFFF' : '#1848A8'
  const dotOpacity = isDark ? 0.55 : 0.45

  function Tile({ x, y }: { x: number; y: number }) {
    const cx = x + T / 2, cy = y + T / 2
    const pad = 4
    const dm = T / 2 - 12
    const dT = cy - dm, dB = cy + dm, dL = cx - dm, dR = cx + dm
    const cr = x + pad, ct = y + pad, cR = x + T - pad, cB = y + T - pad

    return (
      <g>
        <rect x={x} y={y} width={T} height={T} fill={tileFill}/>
        <rect x={cr} y={ct} width={T - pad * 2} height={T - pad * 2}
          fill="none" stroke={lineColor} strokeWidth="1.5"/>
        <polygon points={`${cx},${dT} ${dR},${cy} ${cx},${dB} ${dL},${cy}`}
          fill="none" stroke={lineColor} strokeWidth="1.3"/>
        <path d={`M${dL},${cy} Q${cr},${cy - dm * 0.7} ${cr},${ct}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${cx},${dT} Q${cx - dm * 0.7},${ct} ${cr},${ct}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${cx},${dT} Q${cx + dm * 0.7},${ct} ${cR},${ct}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${dR},${cy} Q${cR},${cy - dm * 0.7} ${cR},${ct}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${dR},${cy} Q${cR},${cy + dm * 0.7} ${cR},${cB}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${cx},${dB} Q${cx + dm * 0.7},${cB} ${cR},${cB}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${cx},${dB} Q${cx - dm * 0.7},${cB} ${cr},${cB}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <path d={`M${dL},${cy} Q${cr},${cy + dm * 0.7} ${cr},${cB}`} fill="none" stroke={lineColor} strokeWidth="1"/>
        <circle cx={cx} cy={cy} r={8} fill="none" stroke={lineColor} strokeWidth="1.3"/>
        <circle cx={cx} cy={cy} r={3} fill={lineColor} fillOpacity={dotOpacity}/>
        <circle cx={cx} cy={dT} r={2.5} fill="none" stroke={lineColor} strokeWidth="1"/>
        <circle cx={dR} cy={cy} r={2.5} fill="none" stroke={lineColor} strokeWidth="1"/>
        <circle cx={cx} cy={dB} r={2.5} fill="none" stroke={lineColor} strokeWidth="1"/>
        <circle cx={dL} cy={cy} r={2.5} fill="none" stroke={lineColor} strokeWidth="1"/>
      </g>
    )
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true"
      style={{ position: 'absolute', right: 0, top: '-5%', height: '110%', width: 'auto', pointerEvents: 'none' }}>
      <defs>
        <linearGradient id="pt-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="white" stopOpacity="0"/>
          <stop offset="25%" stopColor="white" stopOpacity="0.55"/>
          <stop offset="50%" stopColor="white" stopOpacity="1"/>
          <stop offset="100%" stopColor="white" stopOpacity="1"/>
        </linearGradient>
        <mask id="pt-mask">
          <rect width="100%" height="100%" fill="url(#pt-fade)"/>
        </mask>
      </defs>
      <g mask="url(#pt-mask)">
        <rect width={W} height={H} fill={groutBg}/>
        {Array.from({ length: ROWS }, (_, r) =>
          Array.from({ length: COLS }, (_, c) => (
            <Tile key={`${r}-${c}`} x={G + c * (T + G)} y={G + r * (T + G)}/>
          ))
        )}
      </g>
    </svg>
  )
}

// ── Container ────────────────────────────────────────────────────────────────
function Container({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 48px', ...style }}>
      {children}
    </div>
  )
}

// ── Section Label ────────────────────────────────────────────────────────────
function SectionLabel({ children, center }: { children: string; center?: boolean }) {
  return (
    <div style={{
      fontFamily: AF.m, fontSize: 10, color: C.amber,
      letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 10,
      justifyContent: center ? 'center' : undefined,
    }}>
      {children}
      <span style={{ flex: '0 0 32px', height: 1, background: C.amberBorder, display: 'inline-block' }}/>
    </div>
  )
}

// ── HR ───────────────────────────────────────────────────────────────────────
function Hr() {
  return <div style={{ height: 1, background: C.border }}/>
}

// ── Nav ──────────────────────────────────────────────────────────────────────
function Nav() {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: C.bg, borderBottom: `1px solid ${C.border}`,
      height: 64, display: 'flex', alignItems: 'center',
    }}>
      <Container>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32 }}>
          <a href="/" style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', textDecoration: 'none' }}>
            Conjugr<span style={{ color: C.amber }}>8</span>
          </a>
          <div className="c8-nav-links" style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            <a href="#features" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>Features</a>
            <a href="#modes" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>How it works</a>
            <a href="#pricing" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>Pricing</a>
            <a href="#languages" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>Languages</a>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <a href="/learn-spanish" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>Log in</a>
            <a href="/learn-spanish" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: AF.b, fontSize: 15, fontWeight: 700,
              background: C.amber, color: C.bg,
              border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer',
              textDecoration: 'none',
            }}>Start free →</a>
          </div>
        </div>
      </Container>
    </nav>
  )
}

// ── Hero language config ──────────────────────────────────────────────────────
const HERO_LANGS = {
  spanish: {
    label: 'Spanish', flag: '🇪🇸',
    // Dark colors — dark warm parchment, same hue as light
    bg: '#1E0F06', surf: '#2C1A0C', surfBorder: 'rgba(220,160,80,0.10)',
    text: '#F5EDE0', muted: '#C4A88E', dim: '#826050',
    accent: '#D86A00', accentText: '#FFF5E8',
    glow1: 'radial-gradient(ellipse at 15% 60%, rgba(216,106,0,0.18) 0%, transparent 55%)',
    glow2: 'radial-gradient(ellipse at 85% 10%, rgba(233,170,48,0.10) 0%, transparent 45%)',
    // Light colors — warm parchment
    bgL: '#F8F1E5', surfL: '#EDE0CC', surfBorderL: 'rgba(130,96,80,0.16)',
    textL: '#1A0E06', mutedL: '#6C4830', dimL: '#9A7860',
    accentL: '#B43600', accentTextL: '#F8F1E5',
    glow1L: 'radial-gradient(ellipse at 15% 60%, rgba(180,54,0,0.09) 0%, transparent 55%)',
    glow2L: 'radial-gradient(ellipse at 85% 10%, rgba(233,170,48,0.07) 0%, transparent 45%)',
    headingFont: "'Lora', Georgia, serif",
    motifLabel: 'Azulejo · Iberian tile pattern',
    eyebrow: 'Verb Practice · Spanish',
    heading: <>The best way to<br/>learn Spanish.</>,
    sub: 'Drill Spanish conjugations across all 12 tenses — without streaks, coins, or gamification.',
    ctaLabel: 'Start learning Spanish',
    ctaHref: '/learn-spanish',
    links: [
      { label: 'Spanish grammar', href: '/learn-spanish/grammar' },
      { label: 'Vocabulary', href: '/learn-spanish/vocabulary' },
      { label: 'Sentences', href: '/learn-spanish/sentences' },
    ],
  },
  portuguese: {
    label: 'Portuguese', flag: '🇧🇷',
    // Dark colors — deep forest green, same hue as light sage
    bg: '#081410', surf: '#0F2018', surfBorder: 'rgba(80,180,100,0.10)',
    text: '#E4F0E6', muted: '#72A882', dim: '#3E6A50',
    accent: '#C8A830', accentText: '#081410',
    glow1: 'radial-gradient(ellipse at 15% 60%, rgba(20,120,64,0.28) 0%, transparent 55%)',
    glow2: 'radial-gradient(ellipse at 85% 10%, rgba(88,200,140,0.10) 0%, transparent 45%)',
    // Light colors — soft sage green
    bgL: '#F2F8F4', surfL: '#E2EDE5', surfBorderL: 'rgba(46,94,60,0.18)',
    textL: '#0A1A0E', mutedL: '#2E5E3C', dimL: '#6A9278',
    accentL: '#8A6E00', accentTextL: '#F2F8F4',
    glow1L: 'radial-gradient(ellipse at 15% 60%, rgba(20,120,64,0.09) 0%, transparent 55%)',
    glow2L: 'radial-gradient(ellipse at 85% 10%, rgba(24,88,168,0.07) 0%, transparent 45%)',
    headingFont: "'Playfair Display', Georgia, serif",
    motifLabel: 'Azulejo · Porto tile pattern',
    eyebrow: 'Verb Practice · Portuguese',
    heading: <>The best way to<br/>learn Portuguese.</>,
    sub: 'Master Portuguese verb forms with drills, flashcards, sentence builder, and quiz modes.',
    ctaLabel: 'Start learning Portuguese',
    ctaHref: '/learn-portuguese',
    links: [
      { label: 'Portuguese grammar', href: '/learn-portuguese/grammar' },
      { label: 'Vocabulary', href: '/learn-portuguese/vocabulary' },
      { label: 'Sentences', href: '/learn-portuguese/sentences' },
    ],
  },
  finnish: {
    label: 'Finnish', flag: '🇫🇮',
    // Dark — deep midnight blue
    bg: '#070A10', surf: '#0E1420', surfBorder: 'rgba(59,142,234,0.12)',
    text: '#EEF1F5', muted: '#7A90B0', dim: '#3A4A5C',
    accent: '#3B8EEA', accentText: '#F5F9FF',
    glow1: 'radial-gradient(ellipse at 15% 60%, rgba(59,142,234,0.20) 0%, transparent 55%)',
    glow2: 'radial-gradient(ellipse at 85% 10%, rgba(100,180,255,0.10) 0%, transparent 45%)',
    // Light — pale Nordic sky
    bgL: '#F5F8FC', surfL: '#E6EEF8', surfBorderL: 'rgba(31,95,184,0.16)',
    textL: '#0C0E12', mutedL: '#2A4060', dimL: '#6878A0',
    accentL: '#1F5FB8', accentTextL: '#F5F8FC',
    glow1L: 'radial-gradient(ellipse at 15% 60%, rgba(31,95,184,0.08) 0%, transparent 55%)',
    glow2L: 'radial-gradient(ellipse at 85% 10%, rgba(59,142,234,0.06) 0%, transparent 45%)',
    headingFont: "'Space Grotesk', system-ui, sans-serif",
    motifLabel: 'Kalevala · Finnish rune pattern',
    eyebrow: 'Language Practice · Finnish',
    heading: <>The best way to<br/>learn Finnish.</>,
    sub: 'Explore Finnish with drills, flashcards, and sentence builder — built for the world\'s most unique language.',
    ctaLabel: 'Start learning Finnish',
    ctaHref: '/learn-finnish',
    links: [
      { label: 'Finnish grammar', href: '/learn-finnish/grammar' },
      { label: 'Vocabulary', href: '/learn-finnish/vocabulary' },
      { label: 'Sentences', href: '/learn-finnish/sentences' },
    ],
  },
}
type HeroLang = keyof typeof HERO_LANGS

// ── Hero ─────────────────────────────────────────────────────────────────────
function Hero() {
  const [lang, setLang] = React.useState<HeroLang>('spanish')
  const [isDark, setIsDark] = React.useState(true)

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    setIsDark(mq.matches)
    const h = (e: MediaQueryListEvent) => setIsDark(e.matches)
    mq.addEventListener('change', h)
    return () => mq.removeEventListener('change', h)
  }, [])

  const lc = HERO_LANGS[lang]
  // Derive current colors based on dark/light mode
  const cc = {
    bg:          isDark ? lc.bg          : lc.bgL,
    surf:        isDark ? lc.surf        : lc.surfL,
    surfBorder:  isDark ? lc.surfBorder  : lc.surfBorderL,
    text:        isDark ? lc.text        : lc.textL,
    muted:       isDark ? lc.muted       : lc.mutedL,
    dim:         isDark ? lc.dim         : lc.dimL,
    accent:      isDark ? lc.accent      : lc.accentL,
    accentText:  isDark ? lc.accentText  : lc.accentTextL,
    glow1:       isDark ? lc.glow1       : lc.glow1L,
    glow2:       isDark ? lc.glow2       : lc.glow2L,
  }

  return (
    <section style={{
      padding: '88px 0 80px', position: 'relative', overflow: 'hidden',
      background: cc.bg,
      transition: 'background 0.45s',
    }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: cc.glow1, pointerEvents: 'none', transition: 'background 0.45s' }}/>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: cc.glow2, pointerEvents: 'none', transition: 'background 0.45s' }}/>
      {lang === 'spanish' ? <AndalucianDecor isDark={isDark}/> : lang === 'finnish' ? <FinnishDecor isDark={isDark}/> : <PortugalDecor isDark={isDark}/>}
      <div aria-hidden="true" style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 1,
        background: `linear-gradient(to right, ${cc.accent}55 0%, ${cc.accent}22 50%, transparent 100%)`,
        transition: 'background 0.45s',
      }}/>

      <Container style={{ position: 'relative' }}>
        {/* Language toggle — prominent flag cards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 48, alignItems: 'center', flexWrap: 'wrap' }}>
          {(Object.keys(HERO_LANGS) as HeroLang[]).map(k => {
            const active = lang === k
            const lk = HERO_LANGS[k]
            const lkAccent = isDark ? lk.accent : lk.accentL
            return (
              <button key={k} onClick={() => setLang(k)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                padding: '14px 30px', borderRadius: 14,
                border: active ? `2px solid ${lkAccent}` : `1px solid ${cc.surfBorder}`,
                cursor: 'pointer', background: active ? `${lkAccent}18` : cc.surf,
                transition: 'background 0.25s, border-color 0.25s',
              }}>
                <span style={{ fontSize: 30, lineHeight: 1, filter: active ? 'none' : 'saturate(0.5) opacity(0.65)', transition: 'filter 0.25s' }}>{lk.flag}</span>
                <span style={{
                  fontFamily: AF.b, fontSize: 12, fontWeight: active ? 700 : 500,
                  color: active ? lkAccent : cc.dim, letterSpacing: '0.05em', transition: 'color 0.25s',
                }}>{lk.label}</span>
              </button>
            )
          })}
          <span style={{ fontFamily: AF.m, fontSize: 10, color: cc.dim, letterSpacing: '0.12em', marginLeft: 4, transition: 'color 0.45s' }}>
            {lc.motifLabel}
          </span>
        </div>

        <div style={{ maxWidth: 760 }}>
          <div style={{
            fontFamily: AF.m, fontSize: 10, color: cc.accent,
            letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 10,
            transition: 'color 0.45s',
          }}>
            {lc.eyebrow}
            <span style={{ flex: '0 0 32px', height: 1, background: cc.accent, opacity: 0.4, display: 'inline-block', transition: 'background 0.45s' }}/>
          </div>
          <h1 style={{
            fontFamily: lc.headingFont, fontSize: 'clamp(50px, 7vw, 86px)', fontWeight: 700,
            lineHeight: 1.05, letterSpacing: '-2px', color: cc.text, marginBottom: 24,
            transition: 'color 0.45s, font-family 0s',
          }}>
            {lc.heading}
          </h1>
          <p style={{ fontFamily: AF.b, fontSize: 19, color: cc.muted, maxWidth: 500, lineHeight: 1.7, marginBottom: 40, transition: 'color 0.45s' }}>
            {lc.sub}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 40 }}>
            <a href={lc.ctaHref} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: AF.b, fontSize: 15, fontWeight: 700,
              background: cc.accent, color: cc.accentText,
              border: 'none', padding: '13px 28px', borderRadius: 8, cursor: 'pointer',
              textDecoration: 'none', transition: 'background 0.2s',
            }}>{lc.ctaLabel}</a>
            <a href="#modes" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: AF.b, fontSize: 15, fontWeight: 500,
              background: 'transparent', color: cc.muted,
              border: `1px solid ${cc.surfBorder}`, padding: '13px 28px', borderRadius: 8,
              textDecoration: 'none', transition: 'color 0.45s, border-color 0.45s',
            }}>See how it works</a>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 44 }}>
            {lc.links.map(l => (
              <a key={l.href} href={l.href} style={{
                fontFamily: AF.b, fontSize: 12, color: cc.muted,
                background: cc.surf, border: `1px solid ${cc.surfBorder}`,
                padding: '5px 12px', borderRadius: 20, textDecoration: 'none',
                transition: 'color 0.45s, background 0.45s, border-color 0.45s',
              }}>{l.label} →</a>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap', rowGap: 28 }}>
            {[['2,400+','verbs covered'],['12','tenses & moods'],['4','practice modes']].map(([n, l], i) => (
              <React.Fragment key={l}>
                {i > 0 && <div style={{ width: 1, background: cc.surfBorder, margin: '4px 36px 4px 0', transition: 'background 0.45s' }}/>}
                <div style={{ paddingRight: 36 }}>
                  <div style={{ fontFamily: AF.h, fontSize: 36, fontWeight: 700, color: cc.text, letterSpacing: '-1.5px', lineHeight: 1, transition: 'color 0.45s' }}>{n}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 13, color: cc.muted, marginTop: 4, transition: 'color 0.45s' }}>{l}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}

// ── Comparison ───────────────────────────────────────────────────────────────
const comparisonRows = [
  ['Grammar-focused', '–', '✓'],
  ['All 12 tenses', '–', '✓'],
  ['No streaks required', '–', '✓'],
  ['Pronoun-level targeting', '–', '✓'],
  ['Custom flashcard decks', '–', '✓'],
  ['No energy bars', '–', '✓'],
]

function Comparison({ isMobile }: { isMobile: boolean }) {
  return (
    <section style={{ padding: '72px 0' }}>
      <Container>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 40 : 80,
          alignItems: 'center',
        }}>
          <div>
            <SectionLabel>Built different</SectionLabel>
            <h2 style={{
              fontFamily: AF.h, fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 700,
              letterSpacing: '-1.5px', lineHeight: 1.1, color: C.text, marginBottom: 20,
            }}>
              No coins. No streaks.<br/>No nonsense.
            </h2>
            <p style={{ fontFamily: AF.b, fontSize: 16, color: C.muted, lineHeight: 1.75, maxWidth: 420 }}>
              Gamification is great for motivation. It's terrible for grammar. Conjugr8 is designed for adults supplementing other study — people who already know <em>why</em> they're learning and just need efficient repetition.
            </p>
          </div>
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, background: C.surf2 }}>
                <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>FEATURE</span>
              </div>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, background: C.surf2, textAlign: 'center' }}>
                <span style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.1em' }}>OTHERS</span>
              </div>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, background: C.amberBg, textAlign: 'center' }}>
                <span style={{ fontFamily: AF.m, fontSize: 10, color: C.amber, letterSpacing: '0.1em' }}>CONJUGR8</span>
              </div>
              {/* Rows */}
              {comparisonRows.map(([feat, other, ours], i) => {
                const isLast = i === comparisonRows.length - 1
                return (
                  <React.Fragment key={feat}>
                    <div style={{ padding: '12px 16px', borderBottom: isLast ? 'none' : `1px solid ${C.border}` }}>
                      <span style={{ fontFamily: AF.b, fontSize: 14, color: C.text }}>{feat}</span>
                    </div>
                    <div style={{ padding: '12px 16px', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, textAlign: 'center' }}>
                      <span style={{ fontSize: 15, color: C.dim }}>{other}</span>
                    </div>
                    <div style={{ padding: '12px 16px', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, background: C.amberBg, textAlign: 'center' }}>
                      <span style={{ fontSize: 15, color: C.green }}>{ours}</span>
                    </div>
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ── Features ─────────────────────────────────────────────────────────────────
const features = [
  { n: '01', title: 'Conjugation drills', body: 'Type the exact form for any pronoun. Instant feedback — correct or not, every time, no multiple choice guessing.' },
  { n: '02', title: 'Flashcard system', body: 'Build your own deck from our library of 2,400+ cards. Rate each card to surface what you need to review most.' },
  { n: '03', title: 'Sentence builder', body: 'Rearrange word tiles to form a translation. The fastest way to internalize word order in a new language.' },
  { n: '04', title: 'Quiz mode', body: 'Four-option multiple choice for recognition practice. Great for beginners or warming up before a drill session.' },
  { n: '05', title: 'Pronoun targeting', body: 'Drill only the forms you struggle with — filter by yo, nosotros, vosotros, or any combination before starting.' },
  { n: '06', title: 'Progress tracking', body: 'See tense mastery, accuracy over time, and your full list of mastered verbs. Know exactly where to focus.' },
]

function Features({ isMobile }: { isMobile: boolean }) {
  return (
    <section id="features" style={{ padding: '96px 0' }}>
      <Container>
        <SectionLabel>Features</SectionLabel>
        <h2 style={{
          fontFamily: AF.h, fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 700,
          letterSpacing: '-2px', lineHeight: 1.05, color: C.text, marginBottom: 64, maxWidth: 500,
        }}>
          Everything you need.<br/>Nothing you don't.
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 0 }}>
          {features.map((f, i) => {
            const col = i % 3
            const row = Math.floor(i / 3)
            const cols = isMobile ? 1 : 3
            const rows = isMobile ? features.length : Math.ceil(features.length / 3)
            const isLastCol = isMobile ? true : col === cols - 1
            const isLastRow = isMobile ? i === features.length - 1 : row === rows - 1
            return (
              <div key={f.n} style={{
                padding: isMobile ? '28px 0' : '40px 36px',
                borderRight: isLastCol ? 'none' : `1px solid ${C.border}`,
                borderBottom: isLastRow ? 'none' : `1px solid ${C.border}`,
              }}>
                <div style={{ fontFamily: AF.m, fontSize: 11, color: C.amber, letterSpacing: '0.12em', marginBottom: 20 }}>{f.n}</div>
                <h3 style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.4px', marginBottom: 12 }}>{f.title}</h3>
                <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, lineHeight: 1.75 }}>{f.body}</p>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}

// ── How it works ─────────────────────────────────────────────────────────────
const steps = [
  { n: '1', title: 'Choose your focus', body: 'Pick a tense, select which pronouns to include, filter by verb type, and set a session length. Or just hit start with the defaults.' },
  { n: '2', title: 'Drill or review', body: 'Switch between four modes in one tap: conjugation drills, flashcards, sentence construction, or a quick quiz. Same content, different challenge.' },
  { n: '3', title: 'See what sticks', body: 'Your progress dashboard tracks accuracy per tense, your mastered verb list, and weekly practice volume. No fluff.' },
  { n: '4', title: 'Build your deck', body: 'Save any card to your personal flashcard deck as you go. Review your deck anytime between sessions.' },
]

function HowItWorks({ isMobile }: { isMobile: boolean }) {
  return (
    <section id="modes" style={{ padding: '96px 0', background: C.surf }}>
      <Container>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1.4fr',
          gap: isMobile ? 40 : 96,
          alignItems: 'start',
        }}>
          <div style={{ position: isMobile ? 'static' : 'sticky', top: 96 }}>
            <SectionLabel>How it works</SectionLabel>
            <h2 style={{
              fontFamily: AF.h, fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 700,
              letterSpacing: '-1.5px', lineHeight: 1.1, color: C.text, marginBottom: 20,
            }}>
              A session takes<br/>under 5 minutes.
            </h2>
            <p style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, lineHeight: 1.75, marginBottom: 32 }}>
              Open the app, configure what you want to drill, and start. No account required to try.
            </p>
            <a href="/learn-spanish" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: AF.b, fontSize: 15, fontWeight: 700,
              background: C.amber, color: C.bg,
              border: 'none', padding: '13px 28px', borderRadius: 8, cursor: 'pointer',
              textDecoration: 'none',
            }}>Try it now →</a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {steps.map((s, i) => (
              <div key={s.n} style={{
                display: 'flex', gap: 32, padding: '32px 0',
                borderBottom: i < steps.length - 1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{ fontFamily: AF.m, fontSize: 42, fontWeight: 500, color: C.amber, lineHeight: 1, flexShrink: 0, width: 48 }}>{s.n}</div>
                <div>
                  <h3 style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.4px', marginBottom: 10 }}>{s.title}</h3>
                  <p style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, lineHeight: 1.75 }}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}

// ── Languages ────────────────────────────────────────────────────────────────
const langs = [
  { flag: '🇪🇸', name: 'Spanish', sub: 'España · Latinoamérica', status: 'Available', note: 'All 12 tenses · 2,400+ verbs', color: 'var(--accent)', motif: 'm-azulejo', href: '/learn-spanish' },
  { flag: '🇧🇷', name: 'Portuguese', sub: 'Brasil', status: 'Available', note: 'All 12 tenses · 2,400+ verbs', color: 'var(--success)', motif: 'm-calcada', href: '/learn-portuguese' },
  { flag: '🇫🇮', name: 'Finnish', sub: 'Suomi', status: 'Beta', note: 'Present &amp; common tenses', color: 'var(--accent)', motif: 'm-nordic', href: '/learn-finnish' },
  { flag: '🇫🇷', name: 'French', sub: 'France · Québec', status: 'Coming soon', note: 'In development', color: 'var(--dim)', motif: null, href: null },
  { flag: '🇩🇪', name: 'German', sub: 'Deutschland · Österreich', status: 'Coming soon', note: 'Planned', color: 'var(--dim)', motif: null, href: null },
]

function Languages() {
  return (
    <section id="languages" style={{ padding: '96px 0' }}>
      <Container>
        <SectionLabel>Languages</SectionLabel>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 1, background: C.border,
          border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden',
        }}>
          {langs.map(l => {
            const avail = l.status === 'Available'
            return (
              <div key={l.name} style={{ position: 'relative', overflow: 'hidden', padding: '36px 32px', background: C.bg, opacity: avail ? 1 : 0.55 }}>
                {l.motif && (
                  <svg aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.07, pointerEvents: 'none' }}>
                    <rect width="100%" height="100%" fill={`url(#${l.motif})`}/>
                  </svg>
                )}
                <div style={{ position: 'relative' }}>
                  <div style={{ fontSize: 36, marginBottom: 16 }}>{l.flag}</div>
                  <div style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 4 }}>{l.name}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 16 }}>{l.sub}</div>
                  <div style={{
                    display: 'inline-block', fontFamily: AF.m, fontSize: 10,
                    color: l.color, letterSpacing: '0.1em',
                    border: `1px solid ${l.color}`, padding: '4px 10px', borderRadius: 4, marginBottom: 12,
                  }}>{l.status.toUpperCase()}</div>
                  <div style={{ fontFamily: AF.b, fontSize: 12, color: C.dim }}>{l.note}</div>
                  {avail && l.href && (
                    <div style={{ marginTop: 16 }}>
                      <a href={l.href} style={{ fontFamily: AF.b, fontSize: 13, color: l.color, textDecoration: 'none' }}>
                        Start learning →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}

// ── Pricing ──────────────────────────────────────────────────────────────────
const freeFeatures = ['50 verbs per month', 'Present & Preterite tenses', 'Basic progress tracking', 'Drills mode only']
const proFeatures = ['Unlimited verbs', 'All 12 tenses', 'Flashcards + Sentence builder', 'Advanced progress analytics', 'Spanish, Portuguese + Finnish', 'Priority support']

function Pricing({ isMobile }: { isMobile: boolean }) {
  return (
    <section id="pricing" style={{ padding: '96px 0', background: C.surf }}>
      <Container>
        <SectionLabel>Pricing</SectionLabel>
        <h2 style={{
          fontFamily: AF.h, fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 700,
          letterSpacing: '-2px', lineHeight: 1.05, color: C.text, marginBottom: 16,
        }}>
          Simple, honest pricing.
        </h2>
        <p style={{ fontFamily: AF.b, fontSize: 17, color: C.muted, marginBottom: 56 }}>
          No streaks to protect. No energy bars to refill.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 20, maxWidth: 780,
        }}>
          {/* Free */}
          <div style={{ padding: '36px 32px', border: `1px solid ${C.border}`, borderRadius: 12, background: C.bg }}>
            <div style={{ fontFamily: AF.h, fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Free</div>
            <div style={{ fontFamily: AF.h, fontSize: 48, fontWeight: 700, color: C.text, letterSpacing: '-2px', marginBottom: 4 }}>€0</div>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 32 }}>Always free</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
              {freeFeatures.map(f => (
                <div key={f} style={{ display: 'flex', gap: 10 }}>
                  <span style={{ color: C.green, flexShrink: 0 }}>✓</span>
                  <span style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>{f}</span>
                </div>
              ))}
            </div>
            <a href="/learn-spanish" style={{
              display: 'flex', width: '100%', justifyContent: 'center',
              fontFamily: AF.b, fontSize: 15, fontWeight: 500, color: C.muted,
              background: 'transparent', border: `1px solid ${C.border}`,
              padding: '13px 28px', borderRadius: 8, textDecoration: 'none',
            }}>Get started free</a>
          </div>
          {/* Pro */}
          <div style={{ padding: '36px 32px', border: `2px solid ${C.amberBorder}`, borderRadius: 12, background: C.amberBg, position: 'relative' }}>
            <div style={{
              position: 'absolute', top: 16, right: 16,
              background: C.amber, color: C.bg,
              fontFamily: AF.m, fontSize: 9, fontWeight: 700,
              padding: '4px 10px', borderRadius: 4, letterSpacing: '0.08em',
            }}>POPULAR</div>
            <div style={{ fontFamily: AF.h, fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Pro</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: AF.h, fontSize: 48, fontWeight: 700, color: C.text, letterSpacing: '-2px', lineHeight: 1 }}>€8</span>
              <span style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, marginBottom: 8 }}>/month</span>
            </div>
            <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, marginBottom: 32 }}>or €64/year — save 33%</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
              {proFeatures.map(f => (
                <div key={f} style={{ display: 'flex', gap: 10 }}>
                  <span style={{ color: C.amber, flexShrink: 0, fontWeight: 700 }}>✓</span>
                  <span style={{ fontFamily: AF.b, fontSize: 14, color: C.text }}>{f}</span>
                </div>
              ))}
            </div>
            <a href="/learn-spanish" style={{
              display: 'flex', width: '100%', justifyContent: 'center',
              fontFamily: AF.b, fontSize: 15, fontWeight: 700,
              background: C.amber, color: C.bg,
              border: 'none', padding: '13px 28px', borderRadius: 8, textDecoration: 'none',
            }}>Get Pro →</a>
          </div>
        </div>
      </Container>
    </section>
  )
}

// ── Quote ────────────────────────────────────────────────────────────────────
function Quote() {
  return (
    <section style={{ padding: '96px 0' }}>
      <Container>
        <div style={{ maxWidth: 800 }}>
          <div style={{ fontFamily: AF.m, fontSize: 80, color: C.amber, lineHeight: 0.8, marginBottom: 24, opacity: 0.4 }}>"</div>
          <blockquote style={{
            fontFamily: AF.h, fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 500,
            color: C.text, letterSpacing: '-0.8px', lineHeight: 1.45, marginBottom: 28,
          }}>
            I've been using Duolingo for two years and still can't reliably conjugate irregular verbs. Two weeks of daily drills on Conjugr8 and I actually understand the pattern.
          </blockquote>
          <div style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>— Early access user, B2 Spanish learner</div>
        </div>
      </Container>
    </section>
  )
}

// ── CTA ──────────────────────────────────────────────────────────────────────
function CTA() {
  return (
    <section id="signup" style={{ padding: '96px 0', background: C.surf, position: 'relative', overflow: 'hidden' }}>
      <svg aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.05, pointerEvents: 'none' }}>
        <rect width="100%" height="100%" fill="url(#m-azulejo)"/>
      </svg>
      <Container style={{ textAlign: 'center', position: 'relative' }}>
        <SectionLabel center>Start today</SectionLabel>
        <h2 style={{
          fontFamily: AF.h, fontSize: 'clamp(40px, 6vw, 72px)', fontWeight: 700,
          letterSpacing: '-3px', lineHeight: 1.02, color: C.text, marginBottom: 20,
        }}>
          Pick up where<br/>Duolingo left off.
        </h2>
        <p style={{ fontFamily: AF.b, fontSize: 18, color: C.muted, maxWidth: 460, margin: '0 auto 44px', lineHeight: 1.7 }}>
          Free to start. No credit card needed. Your first session takes under 60 seconds to set up.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="/learn-spanish" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: AF.b, fontSize: 16, fontWeight: 700,
            background: C.amber, color: C.bg,
            border: 'none', padding: '15px 36px', borderRadius: 8, textDecoration: 'none',
          }}>Start practicing free</a>
          <a href="#languages" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: AF.b, fontSize: 16, fontWeight: 500,
            background: 'transparent', color: C.muted,
            border: `1px solid ${C.border}`, padding: '15px 36px', borderRadius: 8, textDecoration: 'none',
          }}>🇪🇸 Spanish · 🇧🇷 Portuguese · 🇫🇮 Finnish</a>
        </div>
        <div style={{ fontFamily: AF.m, fontSize: 12, color: C.dim, marginTop: 20, letterSpacing: '0.06em' }}>No account needed to try the first session</div>
      </Container>
    </section>
  )
}

// ── Footer ───────────────────────────────────────────────────────────────────
const footerCols = [
  {
    label: 'Practice',
    links: [
      { label: 'Spanish Practice Hub', href: '/learn-spanish/practice' },
      { label: 'Portuguese Practice Hub', href: '/learn-portuguese/practice' },
      { label: 'Spanish Grammar', href: '/learn-spanish/grammar' },
      { label: 'Portuguese Grammar', href: '/learn-portuguese/grammar' },
      { label: 'Pricing', href: '#pricing' },
    ],
  },
  {
    label: 'Spanish',
    links: [
      { label: 'Vocabulary', href: '/learn-spanish/vocabulary' },
      { label: 'Sentences', href: '/learn-spanish/sentences' },
      { label: 'Speaking', href: '/learn-spanish/speaking' },
      { label: 'Comprehension', href: '/learn-spanish/comprehension' },
      { label: 'Language Practice', href: '/learn-spanish/language-practice' },
    ],
  },
  {
    label: 'Portuguese',
    links: [
      { label: 'Vocabulary', href: '/learn-portuguese/vocabulary' },
      { label: 'Sentences', href: '/learn-portuguese/sentences' },
      { label: 'Speaking', href: '/learn-portuguese/speaking' },
      { label: 'Comprehension', href: '/learn-portuguese/comprehension' },
      { label: 'Language Practice', href: '/learn-portuguese/language-practice' },
    ],
  },
  {
    label: 'Company',
    links: [
      { label: 'Privacy policy', href: '/privacy' },
      { label: 'Terms of service', href: '/terms' },
      { label: 'Contact', href: '/contact' },
    ],
  },
]

function Footer({ isMobile }: { isMobile: boolean }) {
  return (
    <footer style={{ background: C.bg, padding: '56px 0 32px' }}>
      <Container>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : '220px 1fr 1fr 1fr 1fr',
          gap: isMobile ? 28 : 48,
          marginBottom: 48,
        }}>
          {/* Brand */}
          <div style={{ gridColumn: isMobile ? '1 / -1' : undefined }}>
            <div style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', marginBottom: 12 }}>
              Conjugr<span style={{ color: C.amber }}>8</span>
            </div>
            <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 16 }}>
              Fast grammar practice for serious language learners.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <a href="/learn-spanish" style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, textDecoration: 'none' }}>🇪🇸 Spanish</a>
              <a href="/learn-portuguese" style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, textDecoration: 'none' }}>🇧🇷 Portuguese</a>
              <a href="/learn-finnish" style={{ fontFamily: AF.m, fontSize: 11, color: C.dim, textDecoration: 'none' }}>🇫🇮 Finnish</a>
            </div>
          </div>
          {/* Link columns */}
          {footerCols.map(col => (
            <div key={col.label}>
              <div style={{ fontFamily: AF.m, fontSize: 9, color: C.dim, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 16 }}>{col.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                {col.links.map(l => (
                  <a key={l.label} href={l.href} style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, textDecoration: 'none' }}>{l.label}</a>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Hr />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24, flexWrap: 'wrap', gap: 16 }}>
          <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>© 2026 Conjugr8, Inc. All rights reserved.</span>
          <div style={{ display: 'flex', gap: 20 }}>
            <a href="/privacy" style={{ fontFamily: AF.b, fontSize: 12, color: C.dim, textDecoration: 'none' }}>Privacy</a>
            <a href="/terms" style={{ fontFamily: AF.b, fontSize: 12, color: C.dim, textDecoration: 'none' }}>Terms</a>
            <a href="/contact" style={{ fontFamily: AF.b, fontSize: 12, color: C.dim, textDecoration: 'none' }}>Contact</a>
          </div>
        </div>
      </Container>
    </footer>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function MarketingPage() {
  const isMobile = useIsMobile()

  return (
    <div style={{ fontFamily: AF.b, background: C.bg, color: C.text, lineHeight: 1.6 }}>
      <SvgDefs />
      <Nav />
      <Hero />
      <Hr />
      <Comparison isMobile={isMobile} />
      <Hr />
      <Features isMobile={isMobile} />
      <Hr />
      <HowItWorks isMobile={isMobile} />
      <Hr />
      <Languages />
      <Hr />
      <Pricing isMobile={isMobile} />
      <Hr />
      <Quote />
      <Hr />
      <CTA />
      <Hr />
      <Footer isMobile={isMobile} />
    </div>
  )
}
